import * as Sentry from "@sentry/react";
import {
  classifyUiError,
  safeDescription,
  safeUiMessage,
  stableFingerprint,
} from "./observabilitySanitizer";

const OPERATIONAL_ROLES = new Set(["operador", "admin", "superadmin"]);
const sentryEnabled = Boolean(import.meta.env.VITE_SENTRY_DSN);
const operationalReplayEnabled = (
  String(import.meta.env.VITE_SENTRY_OPERATIONAL_REPLAY_ENABLED ?? "true").toLowerCase() !== "false"
);
const INCIDENT_RECORDING_AFTER_MS = 30_000;
const DUPLICATE_UI_EVENT_WINDOW_MS = 1_500;

let currentPage = "unknown";
let authenticatedContext = {
  userId: null,
  tenantId: null,
  role: null,
};
let replayTransition = Promise.resolve();
let replayReturnToBufferTimer = null;
const recentUiEvents = new Map();
const uiErrorTestSeamEnabled = (
  String(import.meta.env.VITE_ROTAFLEX_TEST_SEAMS || "").toLowerCase() === "1"
);

const exposeUiErrorToTestSeam = (eventDetails, emitEvent) => {
  if (!uiErrorTestSeamEnabled || typeof window === "undefined") return;
  const events = Array.isArray(window.__ROTAFLEX_TEST_UI_ERRORS__)
    ? window.__ROTAFLEX_TEST_UI_ERRORS__
    : [];
  events.push({
    ...eventDetails,
    emit_event: Boolean(emitEvent),
    replay_requested: Boolean(emitEvent),
  });
  window.__ROTAFLEX_TEST_UI_ERRORS__ = events.slice(-100);
};

const safeDetails = (details = {}) => {
  const allowedKeys = new Set([
    "route_id",
    "programacao_id",
    "grupo_index",
    "count",
    "status",
    "error_code",
    "http_status",
    "http_method",
    "endpoint",
    "duration_ms",
    "online",
    "visibility",
    "source",
  ]);
  return Object.fromEntries(
    Object.entries(details || {})
      .filter(([key, value]) => allowedKeys.has(key) && ["string", "number", "boolean"].includes(typeof value))
      .map(([key, value]) => [key, typeof value === "string" ? value.slice(0, 160) : value])
  );
};

export const setCurrentOperationalPage = (page) => {
  currentPage = String(page || "unknown").slice(0, 60);
};

export const recordPageNavigation = (page, previousPage = currentPage) => {
  const nextPage = String(page || "unknown").slice(0, 60);
  Sentry.setTag("operational_page", nextPage);
  Sentry.addBreadcrumb({
    category: "navigation.operational",
    message: "operational_page_changed",
    level: "info",
    data: {
      from: String(previousPage || "unknown").slice(0, 60),
      to: nextPage,
    },
  });
  currentPage = nextPage;
};

export const configureAuthenticatedObservability = async ({
  userId,
  tenantId,
  role,
}) => {
  authenticatedContext = {
    userId: userId ? String(userId) : null,
    tenantId: tenantId ? String(tenantId) : null,
    role: role ? String(role) : null,
  };

  Sentry.setUser(authenticatedContext.userId ? { id: authenticatedContext.userId } : null);
  Sentry.setTag("tenant_id", authenticatedContext.tenantId || "none");
  Sentry.setTag("user_role", authenticatedContext.role || "unknown");
  Sentry.setTag("operational_page", currentPage);
  Sentry.addBreadcrumb({
    category: "auth.operational",
    message: "authenticated_session_started",
    level: "info",
    data: {
      tenant_id: authenticatedContext.tenantId,
      role: authenticatedContext.role,
    },
  });

  if (
    !sentryEnabled
    || !operationalReplayEnabled
    || !OPERATIONAL_ROLES.has(authenticatedContext.role)
  ) {
    return;
  }

  const replay = Sentry.getReplay();
  if (!replay) return;
  if (replay.getRecordingMode() === "session") await replay.stop();
  if (!replay.getRecordingMode()) replay.startBuffering();
  Sentry.setTag("operational_replay", "incident_buffer");
};

export const clearAuthenticatedObservability = async () => {
  if (replayReturnToBufferTimer) {
    clearTimeout(replayReturnToBufferTimer);
    replayReturnToBufferTimer = null;
  }
  const replay = Sentry.getReplay();
  if (replay?.getRecordingMode()) {
    await replay.stop();
  }
  authenticatedContext = { userId: null, tenantId: null, role: null };
  Sentry.setUser(null);
  Sentry.setTag("tenant_id", "none");
  Sentry.setTag("user_role", "anonymous");
  Sentry.setTag("operational_replay", "off");
};

const scheduleReplayReturnToBuffer = (replay) => {
  if (replayReturnToBufferTimer) clearTimeout(replayReturnToBufferTimer);
  replayReturnToBufferTimer = setTimeout(() => {
    replayTransition = replayTransition
      .then(async () => {
        if (replay.getRecordingMode() === "session") await replay.stop();
        if (!replay.getRecordingMode()) replay.startBuffering();
        Sentry.setTag("operational_replay", "incident_buffer");
      })
      .catch(() => {});
  }, INCIDENT_RECORDING_AFTER_MS);
};

const captureIncidentReplay = () => {
  if (
    !sentryEnabled
    || !operationalReplayEnabled
    || !authenticatedContext.userId
  ) {
    return Promise.resolve(null);
  }
  const replay = Sentry.getReplay();
  if (!replay) return Promise.resolve(null);

  replayTransition = replayTransition
    .then(async () => {
      if (!replay.getRecordingMode()) replay.startBuffering();
      const replayId = replay.getReplayId() || null;
      if (replay.getRecordingMode() === "buffer") {
        // Envia até 60s anteriores ao aviso e mantém somente uma pequena
        // janela posterior. Não grava nem envia a sessão inteira.
        await replay.flush({ continueRecording: true });
      }
      if (!authenticatedContext.userId) return null;
      scheduleReplayReturnToBuffer(replay);
      Sentry.setTag("operational_replay", "incident_window");
      return replayId || replay.getReplayId() || null;
    })
    .catch(() => null);
  return replayTransition;
};

export const recordOperationalAction = (action, details = {}) => {
  Sentry.addBreadcrumb({
    category: "operation.action",
    message: String(action || "unknown_action").slice(0, 100),
    level: "info",
    data: {
      page: currentPage,
      ...safeDetails(details),
    },
  });
};

export const recordUiError = ({
  message,
  code = null,
  page = currentPage,
  source = "ui",
  details = {},
  emitEvent = true,
} = {}) => {
  const classification = classifyUiError(message, code);
  const messageFingerprint = stableFingerprint(message);
  const eventDetails = {
    page: String(page || currentPage).slice(0, 60),
    source: String(source || "ui").slice(0, 60),
    classification,
    message_sanitized: safeUiMessage(message),
    message_fingerprint: messageFingerprint,
    message_length: String(message || "").length,
    ...safeDetails(details),
  };

  Sentry.addBreadcrumb({
    category: "ui.error_displayed",
    message: classification,
    level: "warning",
    data: eventDetails,
  });
  // Seletor estritamente de teste, eliminado do bundle de produção quando a
  // variável não é definida. Expõe somente o payload já sanitizado — nunca o
  // texto bruto, token, telefone ou conteúdo de formulário.
  exposeUiErrorToTestSeam(eventDetails, emitEvent);

  if (!sentryEnabled || !emitEvent) return null;

  const deduplicationKey = `${eventDetails.page}:${messageFingerprint}`;
  const now = Date.now();
  const lastEmittedAt = recentUiEvents.get(deduplicationKey) || 0;
  recentUiEvents.set(deduplicationKey, now);
  if (recentUiEvents.size > 100) {
    for (const [key, emittedAt] of recentUiEvents) {
      if (now - emittedAt > DUPLICATE_UI_EVENT_WINDOW_MS) recentUiEvents.delete(key);
    }
  }
  if (now - lastEmittedAt < DUPLICATE_UI_EVENT_WINDOW_MS) return null;

  const eventId = Sentry.withScope((scope) => {
    const isOrderFlow = eventDetails.page.startsWith("pedidos");
    // O fluxo de pedidos é o caminho operacional crítico. Esses bloqueios
    // usam nível error para entrar no alerta Sentry já existente, mesmo quando
    // são validações esperadas e não exceções JavaScript.
    scope.setLevel(isOrderFlow ? "error" : "warning");
    scope.setTag("operational_event", "ui_error_displayed");
    scope.setTag("operational_flow", isOrderFlow ? "pedido_route_dispatch" : "other");
    scope.setTag("operator_attention_required", "true");
    scope.setTag("ui_error_code", classification);
    scope.setTag("operational_page", eventDetails.page);
    scope.setFingerprint(["operational-ui-error", eventDetails.page, classification]);
    scope.setContext("ui_error", eventDetails);
    scope.setContext("incident_replay", {
      replay_id: Sentry.getReplay()?.getReplayId() || null,
      capture_mode: "buffer_60s_plus_30s",
    });
    return Sentry.captureMessage(
      isOrderFlow
        ? `Aviso no fluxo de pedidos: ${classification}`
        : "Mensagem de erro exibida ao operador"
    );
  });
  void captureIncidentReplay();
  return eventId;
};

export const recordFormErrors = (errors, page = currentPage) => {
  for (const [field, message] of Object.entries(errors || {})) {
    recordUiError({
      message,
      code: `required_field_${String(field).replace(/[^a-z0-9_]/gi, "_").toLowerCase()}`,
      page,
      source: "form_validation",
    });
  }
};

export const reportOperationalProblem = async ({ category, description = "" } = {}) => {
  const replay = Sentry.getReplay();
  const replayId = replay?.getReplayId() || null;
  const normalizedCategory = String(category || "other").slice(0, 60);
  const descriptionSanitized = safeDescription(description);

  if (!sentryEnabled) {
    return { ok: false, reason: "monitoring_disabled", eventId: null, replayId };
  }

  const eventId = Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("operational_event", "operator_report");
    scope.setTag("report_category", normalizedCategory);
    scope.setTag("operational_page", currentPage);
    scope.setFingerprint(["operator-report", normalizedCategory]);
    scope.setContext("operator_report", {
      category: normalizedCategory,
      description: descriptionSanitized || null,
      page: currentPage,
      replay_id: replayId,
      tenant_id: authenticatedContext.tenantId,
      user_id: authenticatedContext.userId,
    });
    return Sentry.captureMessage("Problema reportado pelo operador");
  });

  await captureIncidentReplay();
  await Sentry.flush(3000).catch(() => false);
  return { ok: true, eventId, replayId: replay?.getReplayId() || replayId };
};
